import React, { useContext, useRef, useEffect, useState } from 'react';
import { PageContext } from '../frontend/pageContext';
import { forEachInMenu } from '../frontend/utils';
import { ClientDocLeafMenu, ClientDocSpaceMenu } from '../types';

interface Props {
  menu: ClientDocSpaceMenu | ClientDocLeafMenu;
  children: JSX.Element[] | JSX.Element;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (boolean) => void;
}

interface MenuItem {
  text: string;
  path: string;
}

const Sidebar = ({
  mobileMenuOpen,
  setMobileMenuOpen,
  children,
  menu,
}: Props) => {
  const sideBar = useRef<HTMLElement>(null);
  const searchBox = useRef<HTMLDivElement>(null);
  const filterBox = useRef<HTMLDivElement>(null);
  const { isStatic, docSpace, spacePdfExists } = useContext(PageContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBar, setFilterBar] = useState(false);
  const [resultsList, setResultsList] = useState<MenuItem[]>([]);
  const [selectedResultIndex, setSelectedResultIndex] = useState<
    number | undefined
  >();

  const handleClick = () => {
    setFilterBar(true);

    document.addEventListener('mousedown', handleClickOutside);
  };

  const handleClickOutside = (event) => {
    if (
      mobileMenuOpen &&
      sideBar.current &&
      !sideBar.current.contains(event.target)
    ) {
      setMobileMenuOpen(false);
      document.body.classList.remove('menu-open');
    }

    if (
      searchBox?.current &&
      !searchBox.current.contains(event.target) &&
      filterBox?.current &&
      !filterBox.current.contains(event.target)
    ) {
      setFilterBar(false);
    }
  };

  useEffect(() => {
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sideBar, mobileMenuOpen]);

  const openKapaSearch = () => {
    (window as any).Kapa.open({ mode: "search", query: searchTerm });
  };

  const submitSearch = async (e) => {
    if (!resultsList.length) {
      const kapaElement = document.querySelector<HTMLElement>('.kapa-search');
      if (kapaElement) {
        openKapaSearch();
      }
      return;
    }
    if (selectedResultIndex) {
      document.location.href = resultsList[selectedResultIndex].path;
    }
  };

  const handleSearchChange = async (e) => {
    const text = e.currentTarget.value;
    const found: MenuItem[] = [];
    await forEachInMenu(menu, (item) => {
      if (
        item.type === 'menuGroup' ||
        item.type === 'externalMenuLink' ||
        item.url.includes('#')
      ) {
        return;
      }

      if (text.length && item.text.toLowerCase().includes(text.toLowerCase())) {
        found.push(item);
      }
    });
    setSearchTerm(text);
    setResultsList(found);
  };

  const handleKeyEvents = async (e) => {
    if (e.key === 'Enter') {
      await submitSearch(e);
    }
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && resultsList.length) {
      const changedIndex = e.key === 'ArrowDown' ? 1 : -1;
      const newIndex =
        !selectedResultIndex || selectedResultIndex < 0
          ? 0
          : (selectedResultIndex + changedIndex) % resultsList.length;
      setSelectedResultIndex(newIndex);
    }
  };

  /**
   * Set the menu width and content margin when the user drags the "split bar".
   * This could certainly be implemented in a more "React-y" way, but since
   *   (1) we don't necessarily want to be super tied to React
   *   (2) we will likely replace all of this when the redesign happens
   * I decided to keep this simple for now.
   */
  const handleMenuResize = () => {
    const SIDEBAR_MIN = 320;
    const SIDEBAR_MAX = window.innerWidth * 0.3;
    const CONTENT_MIN = 200;

    const contentEl = document.querySelector<HTMLElement>('.content-area');
    const bannerEl = document.querySelector<HTMLElement>('.banners-section');
    const footerEl = document.querySelector<HTMLElement>('.suds-footer');
    const asideEl = document.querySelector<HTMLElement>('aside');

    /**
     * Sets menu width and main content left-margin as the user drags.
     */
    function resizeSidebar(ev: MouseEvent) {
      if (
        (ev.pageX > SIDEBAR_MIN || ev.pageX === SIDEBAR_MIN) &&
        ev.pageX < SIDEBAR_MAX &&
        ev.pageX < window.innerWidth - CONTENT_MIN
      ) {
        if (sideBar.current) {
          sideBar.current.style.width = `${ev.pageX}px`;
        }
        if (contentEl) {
          contentEl.style.marginLeft = `${ev.pageX}px`;
          contentEl.style.width = `${window.innerWidth - ev.pageX}px`;
        }
        if (footerEl && ev.pageX) {
          footerEl.style.width = `${window.innerWidth - ev.pageX}px`;
        }
        if (bannerEl && asideEl) {
          asideEl.style.top = `${bannerEl.getBoundingClientRect().bottom}px`;
        }
      }
    }

    /**
     * Removes event listeners when dragging is complete (mouseup).
     */
    function cleanup() {
      document.removeEventListener('mousemove', resizeSidebar);
      setTimeout(() => {
        document.removeEventListener('mouseup', cleanup);
      });
    }

    function windowResize() {
      if (window.innerWidth > 1024) {
        return resizeSidebar(
          new MouseEvent('mousemove', {
            clientX: 320,
          }),
        );
      }
    }

    document.addEventListener('mousemove', resizeSidebar);
    document.addEventListener('mouseup', cleanup);
    window.addEventListener('resize', windowResize);
  };

  return (
    <nav
      ref={sideBar}
      className={`menu nocontent${mobileMenuOpen ? ' is-open' : ''}`}
      data-test-id="desktop-search-bar"
    >
      {!isStatic && (
        <div ref={searchBox} className="search-bar">
          <input id="filter-button" className="filter-button" type="button" />
          <input
            id="search-input"
            className="search-input"
            placeholder="Filter by Title"
            value={searchTerm}
            type="text"
            onChange={handleSearchChange}
            onClick={handleClick}
            onKeyDown={handleKeyEvents}
          />
          <div
            className={`search-site ${filterBar && 'open'}`}
            ref={filterBox}
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => e.key === 'Enter' && handleClick()}
            role="button"
          >
            <ul className={searchTerm.length ? 'results' : 'not-start'}>
              {resultsList.length
                ? resultsList.map((leaf, i) => {
                    return (
                      <li
                        key={i}
                        className={
                          selectedResultIndex === i ?
                            'result selected' :
                            'result'
                        }
                      >
                        <a href={leaf.path}>{leaf.text}</a>
                      </li>
                    );
                  })
                : !!searchTerm.length && (
                    <li className="result kapa-search">
                      <button 
                        onClick={openKapaSearch}
                      >
                        Search for &quot;{searchTerm}&quot; with KapaAi
                      </button>
                    </li>
                  )}
            </ul>
          </div>
        </div>
      )}
      {children}
      {docSpace && spacePdfExists && (
        <a
          data-test-id="space-download"
          className="download-button"
          href={`/${docSpace.id}/${docSpace.semver ?? docSpace.version}/assets/${docSpace.id}.pdf`}
          // eslint-disable-next-line react/jsx-no-target-blank
          target="_blank"
        >
          <img src="/assets/images/pdf-icon.svg" alt="pdf-icon" />
          <span>Download PDF</span>
          <span className="tooltip-text">{`Download ${docSpace.title} PDF`}</span>
        </a>
      )}
      <div
        id="split-bar"
        role="button"
        tabIndex={0}
        onMouseDown={handleMenuResize}
        onKeyDown={() => {}}
      ></div>
    </nav>
  );
};

export default Sidebar;
